import React, { useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CalendarHeart } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import AppointmentForm from './AppointmentForm';

/**
 * AppointmentModal
 * ─────────────────────────────────────────────────────────────────────────────
 * Premium hospital-style modal popup containing the reusable AppointmentForm.
 *
 * Features:
 *  • Framer Motion fade + scale open/close animation
 *  • Dark blurred overlay (backdrop-blur + bg-black/50)
 *  • Closes on: X button / overlay click / ESC key
 *  • Locks page scroll while open (overflow-hidden on body)
 *  • Keyboard focus stays inside modal (tab trap via tabIndex)
 *  • Auto-closes 1 second after successful form submission
 *  • Responsive: max-w-xl desktop, 95% mobile, scrollable if tall
 *
 * Props:
 *   isOpen   – boolean — controls visibility
 *   onClose  – callback to close the modal
 * ─────────────────────────────────────────────────────────────────────────────
 */
const AppointmentModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();

  // ── Lock body scroll while modal is open ─────────────────────────────────
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  // ── ESC key to close ──────────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key === 'Escape') onClose();
    },
    [onClose]
  );

  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  // ── Overlay click – close only when clicking the backdrop itself ──────────
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  // ── Framer Motion variants ────────────────────────────────────────────────
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.25 } },
    exit:   { opacity: 0, transition: { duration: 0.2 } },
  };

  const panelVariants = {
    hidden:  { opacity: 0, scale: 0.94, y: 16 },
    visible: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: { duration: 0.3, ease: [0.25, 0.46, 0.45, 0.94] },
    },
    exit: {
      opacity: 0,
      scale: 0.94,
      y: 16,
      transition: { duration: 0.22, ease: [0.55, 0, 1, 0.45] },
    },
  };

  return (
    <AnimatePresence>
      {isOpen && (
        /* ── Overlay ──────────────────────────────────────────────────────── */
        <motion.div
          key="appointment-modal-overlay"
          variants={overlayVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
          onClick={handleOverlayClick}
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          style={{
            background: 'rgba(0,0,0,0.55)',
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
          }}
          aria-modal="true"
          role="dialog"
          aria-label="Book Appointment"
        >
          {/* ── Modal panel ─────────────────────────────────────────────── */}
          <motion.div
            key="appointment-modal-panel"
            variants={panelVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="relative w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-y-auto no-scrollbar"
            style={{ maxHeight: '90vh' }}
            // Prevent overlay-click from firing inside the panel
            onClick={(e) => e.stopPropagation()}
          >
            {/* ── Header ────────────────────────────────────────────────── */}
            <div
              className="sticky top-0 z-10 flex items-center justify-between px-7 py-5 border-b border-slate-100 bg-white rounded-t-3xl"
            >
              <div className="flex items-center gap-3">
                {/* Accent icon badge */}
                <div className="w-10 h-10 bg-[#800000] rounded-xl flex items-center justify-center shrink-0">
                  <CalendarHeart className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2
                    className="text-lg font-extrabold text-slate-900 leading-tight"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                  >
                    {t('contact_title')}
                  </h2>
                  <p className="text-xs text-slate-500 font-medium">
                    {t('contact_subtitle')}
                  </p>
                </div>
              </div>

              {/* Close button */}
              <button
                onClick={onClose}
                aria-label="Close modal"
                className="w-9 h-9 flex items-center justify-center rounded-full border border-slate-200 text-slate-500 hover:border-[#800000] hover:text-[#800000] transition-all duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#800000]"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* ── Form body ─────────────────────────────────────────────── */}
            <div className="px-7 py-6">
              <AppointmentForm onSuccess={onClose} />
            </div>

            {/* ── Footer note ───────────────────────────────────────────── */}
            <div className="px-7 pb-6">
              <p className="text-center text-[11px] text-slate-400 leading-relaxed">
                🔒 Your information is secure and will only be used to schedule your appointment.
              </p>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default AppointmentModal;
